import React, { useEffect, useState } from "react";
import { Table, Button, Row, Col, Tooltip, Popconfirm, Drawer, Grid } from "antd";
import { PlusOutlined, DeleteOutlined, EditOutlined, QuestionCircleOutlined} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchBranchTransactionList,
  deleteBranchTransaction,
} from "../branchTransactionThunks";
import { App } from "antd";
import { selectBranchTransactionList } from "../branchTransactionSelectors";
import {
  setSelectedBranchTransaction,
  setIsEditBranchTransaction,
} from "../branchTransactionSlice";
import commaNumber from "comma-number";
import { formatDateOnly, getAccessType } from "../../../lib/functions";
import { useIsAdminUser } from "../../../lib/accessList";
import { useTxLock } from "../../../hooks/useTxLock";

const format = commaNumber.bindWith(",", ".");

const BranchTransactionList = () => {
  const { modal } = App.useApp();
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isAdminUser = useIsAdminUser();
  const authInfo = useSelector((state) => state.auth?.auth_info);

  const canEdit = authInfo?.can_edit === 1;
  const canDelete = authInfo?.can_delete === 1;
const { isLocked: isTxLocked, lockDateFormatted } = useTxLock();
  let [access_type, access_type_set] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [helpOpen, setHelpOpen] = useState(false);

  useEffect(() => {
    const access = getAccessType("branch_tran");
    access_type_set(access);
  }, []);

  const branchTransactionList = useSelector(selectBranchTransactionList);

  const handleDelete = async (tran_id) => {
    try {
      await dispatch(deleteBranchTransaction(tran_id)).unwrap();
      modal.success({ title: "Branch transaction deleted successfully" });
      dispatch(fetchBranchTransactionList());
    } catch (error) {
      modal.warning({ title: error.message || "Delete failed" });
    }
  };

  const handleEdit = (record) => {
    dispatch(setSelectedBranchTransaction(record));
    dispatch(setIsEditBranchTransaction(true));
    navigate("addNewBranchTransaction");
  };

  const columns = [
    { title: "No.", render: (_, __, index) => index + 1 },
    { title: "Transaction Code", dataIndex: "tran_code" },
    {
      title: "Date",
      dataIndex: "tran_date",
      render: (d) => formatDateOnly(d),
    },
    { title: "To Branch", dataIndex: "name" },
    { title: "From Account", dataIndex: "from_acc_name" },
    { title: "To Branch Account", dataIndex: "to_acc_name" },
    {
      title: "Amount",
      dataIndex: "tran_amount",
      render: (val) => format(Number(val || 0).toFixed(2)),
    },
    { title: "Description", dataIndex: "narration" },
    { title: "Created By", dataIndex: "full_name" },
    {
      title: "Status",
      dataIndex: "status",
      render: (v) => (v === "a" ? "Active" : "Pending"),
    },
{
  title: "Actions",
  render: (_, record) => {
    const txLocked = isTxLocked(record?.tran_date);

    return (
      <>
        <Tooltip
          title={
            txLocked
              ? `This voucher is locked. Transactions up to ${lockDateFormatted} cannot be modified.`
              : "Edit"
          }
        >
          <Button
            type="link"
            onClick={() => {
              if (txLocked) return;

              handleEdit(record);
            }}
            icon={<EditOutlined />}
            disabled={!(isAdminUser || canEdit) || txLocked}
          />
        </Tooltip>

        <Tooltip
          title={
            txLocked
              ? `This voucher is locked. Transactions up to ${lockDateFormatted} cannot be modified.`
              : "Delete"
          }
        >
          <Popconfirm
            title="Are you sure you want to delete this sale?"
            okText="Yes, Delete"
            cancelText="Cancel"
            disabled={!(isAdminUser || canDelete) || txLocked}
            onConfirm={() => {
              if (txLocked) return;

              handleDelete(record.tran_id);
            }}
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              disabled={!(isAdminUser || canDelete) || txLocked}
            />
          </Popconfirm>
        </Tooltip>
      </>
    );
  },
}
  ];

  useEffect(() => {
    dispatch(fetchBranchTransactionList());
  }, [dispatch]);

  return (
    <>
      <Row justify="end" style={{ marginBottom: "32px", marginTop: "32px" }}>
        <Col>
          <Tooltip title="Help">
            <Button
              type="text"
              icon={<QuestionCircleOutlined style={{ fontSize: 18 }} />}
              onClick={() => setHelpOpen(true)}
            />
          </Tooltip>
        </Col>
        <Col>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              dispatch(setSelectedBranchTransaction(null));
              dispatch(setIsEditBranchTransaction(false));
              navigate("addNewBranchTransaction");
            }}
            data-testid="btn-add-branch-tran"
          >
            Add New
          </Button>
        </Col>
      </Row>

      <Table
        columns={columns}
        dataSource={branchTransactionList}
        rowKey="tran_id"
        scroll={{ x: "max-content" }}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: branchTransactionList.length,
          showQuickJumper: true,
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 50, 100],
          onChange: (page) => {
            setCurrentPage(page);
          },
          onShowSizeChange: (_, size) => {
            setPageSize(size);
            setCurrentPage(1);
          },
        }}
      />
      <Drawer
        title="Branch Transaction Help"
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        width={420}
        styles={{
          wrapper: {
            paddingTop: !screens.md ? "80px" : undefined
          }
        }}
      >
        <div style={{ lineHeight: 1.8 }}>
          <h2
            style={{
              fontSize: "18px",
              fontWeight: 600,
              marginBottom: "12px",
              marginLeft: "8px",
            }}
          >
            Branch Transactions
          </h2>

          <p
            style={{
              color: "#555",
              marginBottom: "16px",
              marginLeft: "8px",
            }}
          >
            The Branch Transaction module is used to manage financial transactions
            between company branches within the ERP system. It helps maintain
            accurate branch account balances and transaction tracking.
          </p>

          <div
            style={{
              background: "#f5f5f5",
              padding: "14px",
              borderRadius: "8px",
              marginBottom: "16px",
            }}
          >
            <strong>Available Actions</strong>

            <ol style={{ paddingLeft: "20px", marginTop: "10px" }}>
              <li>View branch transaction records</li>
              <li>Use <strong>Add New</strong> to create transactions</li>
              <li>Edit transaction details when required</li>
              <li>Delete transactions if permitted</li>
              <li>Transfer amounts between branch accounts</li>
              <li>Track transaction status and branch balances</li>
              <li>Monitor narration and transaction history</li>
            </ol>
          </div>

          <div
            style={{
              background: "#fafafa",
              border: "1px solid #eee",
              padding: "14px",
              borderRadius: "8px",
            }}
          >
            <strong>Important Notes</strong>

            <ul
              style={{
                paddingLeft: "18px",
                marginTop: "10px",
                color: "#666",
              }}
            >
              <li>Branch transactions affect branch financial balances immediately.</li>
              <li>Status indicates whether transactions are active or pending.</li>
              <li>Access permissions control edit and delete actions.</li>
              <li>Transaction history is maintained for reporting and auditing.</li>
            </ul>
          </div>
        </div>
      </Drawer>
    </>
  );
};

export default BranchTransactionList;
